import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StaffNotation from '@/components/feature/StaffNotation';
import PianoKeyboard from '@/components/feature/PianoKeyboard';
import { getNote } from '@/lib/music/notes';
import { isOnLine } from '@/lib/music/staff';
import { playMidi } from '@/lib/audio/synth';
import {
  emptyStageProgress,
  type LessonProgress,
  type StageProgress,
} from '@/hooks/useProgress';

const C3_B3 = ['C3', 'D3', 'E3', 'F3', 'G3', 'A3', 'B3'];
const C4_B4 = ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4'];
const C5_B5 = ['C5', 'D5', 'E5', 'F5', 'G5', 'A5', 'B5'];
const FULL = [...C3_B3, ...C4_B4, ...C5_B5];

type StageKind = 'intro' | 'quiz' | 'demo' | 'guided' | 'drill' | 'speed' | 'done';

interface MasteryPolicy {
  minAttempts: number;
  target: number;
  gain: number;
  loss: number;
  rollbackAfter: number;
  rollbackAt: number;
}

interface RegionConfig {
  lessonId: number;
  label: string;
  shortLabel: string;
  notes: string[];
  keyboardStart: number;
  keyboardEnd: number;
  introTitle: string;
  introText: string;
  includeClefQuiz?: boolean;
  doneTitle: string;
  doneText: string;
}

interface StageDef {
  key: string;
  kind: StageKind;
  eyebrow: string;
  title: string;
  text: string;
  notes?: string[];
  showNames?: boolean;
  timeLimit?: number;
  introMode?: 'clef' | 'region';
}

function masteryPolicy(stage: StageDef): MasteryPolicy | null {
  if (stage.key.endsWith('-lines') || stage.key.endsWith('-spaces')) {
    return { minAttempts: 20, target: 90, gain: 4, loss: 6, rollbackAfter: 12, rollbackAt: 45 };
  }
  if (stage.key.endsWith('-mixed')) {
    return { minAttempts: 30, target: 95, gain: 3, loss: 7, rollbackAfter: 15, rollbackAt: 50 };
  }
  if (stage.key.endsWith('-speed-6')) {
    return { minAttempts: 30, target: 95, gain: 3, loss: 8, rollbackAfter: 15, rollbackAt: 48 };
  }
  if (stage.key.endsWith('-speed-4')) {
    return { minAttempts: 40, target: 97, gain: 2, loss: 9, rollbackAfter: 18, rollbackAt: 45 };
  }
  if (stage.key.endsWith('-speed-2')) {
    return { minAttempts: 50, target: 98, gain: 2, loss: 10, rollbackAfter: 20, rollbackAt: 42 };
  }
  return null;
}

const REGION_CONFIG: Record<number, RegionConfig> = {
  1: {
    lessonId: 1,
    label: 'Clave de sol · Região central',
    shortLabel: 'Dó4–Si4',
    notes: C4_B4,
    keyboardStart: 60,
    keyboardEnd: 71,
    introTitle: 'A clave de sol dá a referência da pauta',
    introText:
      'O pentagrama tem cinco linhas. Na clave de sol, o desenho da clave envolve a segunda linha. Essa linha passa a representar a nota Sol.',
    includeClefQuiz: true,
    doneTitle: 'Região Dó4–Si4 dominada',
    doneText:
      'Você reconhece as sete notas centrais em linhas, espaços, ordem aleatória e sob pressão de tempo.',
  },
  2: {
    lessonId: 2,
    label: 'Região aguda',
    shortLabel: 'Dó5–Si5',
    notes: C5_B5,
    keyboardStart: 72,
    keyboardEnd: 83,
    introTitle: 'Agora a leitura continua acima do Si',
    introText:
      'A sequência de nomes continua normalmente: depois do Si4 vem Dó5, Ré5, Mi5 e assim por diante. Vamos aplicar exatamente o mesmo método na região aguda.',
    doneTitle: 'Região aguda dominada',
    doneText:
      'Você já reconhece Dó5 a Si5 sem depender dos nomes escritos e também sob limite de tempo.',
  },
  3: {
    lessonId: 3,
    label: 'Região grave',
    shortLabel: 'Dó3–Si3',
    notes: C3_B3,
    keyboardStart: 48,
    keyboardEnd: 59,
    introTitle: 'Agora vamos descer abaixo do Dó central',
    introText:
      'As linhas suplementares continuam a pauta para baixo. O princípio não muda: linha, espaço, linha, espaço, sempre seguindo a sequência das notas.',
    doneTitle: 'Região grave dominada',
    doneText:
      'Você reconhece Dó3 a Si3 e já entende as linhas suplementares abaixo do Dó central.',
  },
  4: {
    lessonId: 4,
    label: 'Desafio de leitura',
    shortLabel: 'Dó3–Si5',
    notes: FULL,
    keyboardStart: 48,
    keyboardEnd: 83,
    introTitle: 'Agora não existe mais “região fácil”',
    introText:
      'Vamos misturar tudo: abaixo do Dó central, região central e notas agudas. O objetivo é olhar a posição e responder no teclado sem contar linha por linha.',
    doneTitle: 'Mapa completo de notas dominado',
    doneText:
      'Você concluiu o ciclo de reconhecimento. Agora podemos entrar na simbologia musical mantendo a leitura de notas ativa.',
  },
};

function buildStages(config: RegionConfig): StageDef[] {
  const asc = config.notes;
  const desc = [...config.notes].reverse();
  const lineNotes = config.notes.filter((id) => isOnLine(getNote(id).step));
  const spaceNotes = config.notes.filter((id) => !isOnLine(getNote(id).step));
  const prefix = `l${config.lessonId}`;

  const stages: StageDef[] = [
    {
      key: `${prefix}-intro`,
      kind: 'intro',
      eyebrow: 'Entenda primeiro',
      title: config.introTitle,
      text: config.introText,
      introMode: config.includeClefQuiz ? 'clef' : 'region',
      notes: config.notes,
    },
  ];

  if (config.includeClefQuiz) {
    stages.push({
      key: `${prefix}-staff-quiz`,
      kind: 'quiz',
      eyebrow: 'Confira se entendeu',
      title: 'Quantas linhas tem o pentagrama?',
      text: 'Escolha uma resposta. Você só avança depois de acertar.',
    });
  }

  stages.push(
    {
      key: `${prefix}-asc-named`,
      kind: 'demo',
      eyebrow: 'Observe e escute',
      title: `Subindo por ${config.shortLabel}`,
      text: 'Acompanhe cada posição. O nome aparece junto para ligar posição, nome e som.',
      notes: asc,
      showNames: true,
    },
    {
      key: `${prefix}-desc-named`,
      kind: 'demo',
      eyebrow: 'Agora ao contrário',
      title: `Descendo por ${config.shortLabel}`,
      text: 'Veja a mesma região no sentido contrário e memorize o caminho visual.',
      notes: desc,
      showNames: true,
    },
    {
      key: `${prefix}-asc-visual`,
      kind: 'demo',
      eyebrow: 'Memória visual',
      title: 'Subindo sem mostrar o nome',
      text: 'O nome desaparece. Diga mentalmente a nota antes da próxima posição.',
      notes: asc,
      showNames: false,
    },
    {
      key: `${prefix}-desc-visual`,
      kind: 'demo',
      eyebrow: 'Memória visual',
      title: 'Descendo sem mostrar o nome',
      text: 'Faça a leitura visual no sentido contrário sem depender de texto.',
      notes: desc,
      showNames: false,
    },
    {
      key: `${prefix}-guided-up`,
      kind: 'guided',
      eyebrow: 'Sua vez',
      title: 'Toque a sequência ascendente',
      text: 'A nota fica parada até você tocar a tecla correta. Um erro não faz a aula avançar.',
      notes: asc,
    },
    {
      key: `${prefix}-guided-down`,
      kind: 'guided',
      eyebrow: 'Sua vez',
      title: 'Toque a sequência descendente',
      text: 'Agora faça a mesma região no sentido contrário.',
      notes: desc,
    },
    {
      key: `${prefix}-lines`,
      kind: 'drill',
      eyebrow: 'Treino de domínio',
      title: 'Somente notas sobre linhas',
      text: 'As notas de linha aparecem aleatoriamente. Aqui a meta é mais leve: 20 exercícios e 90% de domínio.',
      notes: lineNotes,
    },
    {
      key: `${prefix}-spaces`,
      kind: 'drill',
      eyebrow: 'Treino de domínio',
      title: 'Somente notas nos espaços',
      text: 'Agora só aparecem posições de espaço. Faça 20 exercícios e chegue a 90% de domínio antes de misturar tudo.',
      notes: spaceNotes,
    },
    {
      key: `${prefix}-mixed`,
      kind: 'drill',
      eyebrow: 'Treino de domínio',
      title: 'Linhas e espaços misturados',
      text: 'Agora começa a dificuldade real: linhas e espaços aparecem sem padrão. Faça pelo menos 30 exercícios e alcance 95% de domínio.',
      notes: asc,
    },
    {
      key: `${prefix}-speed-6`,
      kind: 'speed',
      eyebrow: 'Velocidade 1',
      title: 'Reconheça em até 6 segundos',
      text: 'A leitura começa a ficar automática. Faça pelo menos 30 exercícios e mantenha 95% dentro do tempo.',
      notes: asc,
      timeLimit: 6,
    },
    {
      key: `${prefix}-speed-4`,
      kind: 'speed',
      eyebrow: 'Velocidade 2',
      title: 'Reconheça em até 4 segundos',
      text: 'Menos tempo para raciocinar. São pelo menos 40 exercícios e 97% de domínio.',
      notes: asc,
      timeLimit: 4,
    },
    {
      key: `${prefix}-speed-2`,
      kind: 'speed',
      eyebrow: 'Velocidade 3',
      title: 'Reconheça em até 2,5 segundos',
      text: 'Último teste desta região: 50 exercícios, 98% de domínio e resposta quase imediata.',
      notes: asc,
      timeLimit: 2.5,
    },
    {
      key: `${prefix}-complete`,
      kind: 'done',
      eyebrow: 'Etapa dominada',
      title: config.doneTitle,
      text: config.doneText,
    },
  );

  return stages;
}

interface Props {
  lessonId: number;
  reviewAccess?: boolean;
  progress: LessonProgress;
  updateProgress: (updater: (previous: LessonProgress) => LessonProgress) => void;
  onAttempt: (correct: boolean) => void;
}

function historicalAccuracy(score: StageProgress) {
  if (score.attempts === 0) return 0;
  return Math.round((score.correct / score.attempts) * 100);
}

function masteryScore(score: StageProgress) {
  if (typeof score.masteryScore === 'number') {
    return Math.max(0, Math.min(100, Math.round(score.masteryScore)));
  }
  return score.attempts > 0 ? historicalAccuracy(score) : 50;
}

function chooseNote(pool: string[], previous: string | null) {
  const candidates = pool.length > 1 ? pool.filter((id) => id !== previous) : pool;
  return candidates[Math.floor(Math.random() * candidates.length)] ?? pool[0] ?? 'C4';
}

export default function FoundationCarousel({ lessonId, progress, updateProgress, onAttempt, reviewAccess = false }: Props) {
  const config = REGION_CONFIG[lessonId] ?? REGION_CONFIG[1];
  const stages = useMemo(() => buildStages(config), [config]);
  const [reviewStageIndex, setReviewStageIndex] = useState(Math.min(progress.stageIndex ?? 0, stages.length - 1));
  const stageIndex = reviewAccess ? reviewStageIndex : Math.min(progress.stageIndex ?? 0, stages.length - 1);
  const stage = stages[stageIndex];
  const stageScore = progress.stageData[stage.key] ?? emptyStageProgress();
  const policy = masteryPolicy(stage);
  const currentMastery = masteryScore(stageScore);
  const effectiveMastered =
    stageScore.mastered ||
    Boolean(
      policy &&
        stageScore.attempts >= policy.minAttempts &&
        currentMastery >= policy.target,
    );

  const [demoIndex, setDemoIndex] = useState(0);
  const [demoRunning, setDemoRunning] = useState(false);
  const [guidedIndex, setGuidedIndex] = useState(0);
  const [quizMessage, setQuizMessage] = useState('');
  const [target, setTarget] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [rollbackNotice, setRollbackNotice] = useState('');
  const [timeLeft, setTimeLeft] = useState(stage.timeLimit ?? 0);
  const lastTargetRef = useRef<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const currentDemoNote = stage.notes?.[demoIndex] ?? config.notes[0] ?? 'C4';
  const currentGuidedNote = stage.notes?.[guidedIndex] ?? config.notes[0] ?? 'C4';
  const currentNote = target ?? currentGuidedNote;

  const saveStage = useCallback(
    (key: string, updater: (previous: StageProgress) => StageProgress) => {
      updateProgress((previous) => ({
        ...previous,
        stageData: {
          ...previous.stageData,
          [key]: updater(previous.stageData[key] ?? emptyStageProgress()),
        },
      }));
    },
    [updateProgress],
  );

  const markStageMastered = useCallback(
    (key = stage.key) => {
      saveStage(key, (previous) => ({ ...previous, mastered: true }));
    },
    [saveStage, stage.key],
  );

  useEffect(() => {
    if (policy && effectiveMastered && !stageScore.mastered) {
      markStageMastered(stage.key);
    }
  }, [effectiveMastered, markStageMastered, policy, stage.key, stageScore.mastered]);

  const goNext = useCallback(() => {
    setRollbackNotice('');
    const next = Math.min(stageIndex + 1, stages.length - 1);
    if (reviewAccess) {
      setReviewStageIndex(next);
      return;
    }
    updateProgress((previous) => ({
      ...previous,
      stageIndex: next,
      mastered: next === stages.length - 1 ? true : previous.mastered,
    }));
  }, [reviewAccess, stageIndex, stages.length, updateProgress]);

  const goBack = () => {
    setRollbackNotice('');
    if (reviewAccess) {
      setReviewStageIndex(Math.max(0, stageIndex - 1));
      return;
    }
    updateProgress((previous) => ({
      ...previous,
      stageIndex: Math.max(0, stageIndex - 1),
    }));
  };

  const rollbackOneStage = useCallback(() => {
    let targetIndex = stageIndex - 1;
    while (
      targetIndex > 0 &&
      stages[targetIndex].kind !== 'drill' &&
      stages[targetIndex].kind !== 'guided'
    ) {
      targetIndex -= 1;
    }

    if (targetIndex < 0) return;

    if (reviewAccess) {
      setReviewStageIndex(targetIndex);
      setRollbackNotice('Vamos revisar uma etapa anterior. Na sua conta de revisão, você pode voltar a qualquer etapa pelo seletor.');
      return;
    }
    const targetStage = stages[targetIndex];
    updateProgress((previous) => {
      const previousStage = previous.stageData[targetStage.key] ?? emptyStageProgress();
      return {
        ...previous,
        stageIndex: targetIndex,
        stageData: {
          ...previous.stageData,
          [targetStage.key]: {
            ...previousStage,
            mastered: false,
            masteryScore: Math.min(masteryScore(previousStage), 70),
          },
        },
      };
    });

    setRollbackNotice(
      'Seu domínio caiu bastante. O sistema voltou uma etapa para reforçar a base antes de continuar.',
    );
  }, [reviewAccess, stageIndex, stages, updateProgress]);

  useEffect(() => {
    setDemoIndex(0);
    setDemoRunning(false);
    setGuidedIndex(0);
    setQuizMessage('');
    setFeedback(null);
    setFeedbackText('');
    setTarget(null);
    setTimeLeft(stage.timeLimit ?? 0);
    lastTargetRef.current = null;
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
  }, [stageIndex, stage.timeLimit]);

  useEffect(() => {
    if (!demoRunning || stage.kind !== 'demo' || !stage.notes?.length) return undefined;
    playMidi(getNote(stage.notes[demoIndex]).midi, 0.72);
    const timeout = window.setTimeout(() => {
      if (demoIndex + 1 >= stage.notes!.length) {
        setDemoRunning(false);
        markStageMastered();
      } else {
        setDemoIndex((value) => value + 1);
      }
    }, lessonId === 4 ? 620 : 850);
    return () => window.clearTimeout(timeout);
  }, [demoIndex, demoRunning, lessonId, markStageMastered, stage.kind, stage.notes]);

  const newTarget = useCallback(() => {
    if (!stage.notes?.length) return;
    const noteId = chooseNote(stage.notes, lastTargetRef.current);
    lastTargetRef.current = noteId;
    setTarget(noteId);
    setFeedback(null);
    setFeedbackText('');
    if (stage.kind === 'speed') setTimeLeft(stage.timeLimit ?? 0);
  }, [stage.kind, stage.notes, stage.timeLimit]);

  useEffect(() => {
    if ((stage.kind === 'drill' || stage.kind === 'speed') && !target && !effectiveMastered) {
      newTarget();
    }
  }, [effectiveMastered, newTarget, stage.kind, target]);

  const registerDrillResult = useCallback(
    (correct: boolean) => {
      if (!policy) {
        return { masteredNow: false, shouldRollback: false, nextMastery: currentMastery };
      }

      const nextAttempts = stageScore.attempts + 1;
      const nextMastery = Math.max(
        0,
        Math.min(100, currentMastery + (correct ? policy.gain : -policy.loss)),
      );
      const masteredNow =
        nextAttempts >= policy.minAttempts && nextMastery >= policy.target;
      const shouldRollback =
        !correct &&
        nextAttempts >= policy.rollbackAfter &&
        nextMastery <= policy.rollbackAt;

      saveStage(stage.key, (previous) => ({
        ...previous,
        attempts: previous.attempts + 1,
        correct: previous.correct + (correct ? 1 : 0),
        wrong: previous.wrong + (correct ? 0 : 1),
        bestStreak: correct ? previous.bestStreak + 1 : 0,
        masteryScore: nextMastery,
        mastered: previous.mastered || masteredNow,
      }));

      updateProgress((previous) => ({
        ...previous,
        attempts: previous.attempts + 1,
        correct: previous.correct + (correct ? 1 : 0),
        wrong: previous.wrong + (correct ? 0 : 1),
        bestStreak: correct ? previous.bestStreak + 1 : 0,
      }));

      onAttempt(correct);
      return { masteredNow, shouldRollback, nextMastery };
    },
    [currentMastery, onAttempt, policy, saveStage, stage.key, stageScore.attempts, updateProgress],
  );

  const handleKeyboard = (midi: number) => {
    if (stage.kind === 'guided') {
      if (feedback === 'correct' || effectiveMastered) return;
      const expected = getNote(currentGuidedNote).midi;
      const correct = midi === expected;
      updateProgress((previous) => ({
        ...previous,
        attempts: previous.attempts + 1,
        correct: previous.correct + (correct ? 1 : 0),
        wrong: previous.wrong + (correct ? 0 : 1),
      }));
      onAttempt(correct);
      if (!correct) {
        setFeedback('wrong');
        setFeedbackText(`Ainda não. A nota continua sendo ${getNote(currentGuidedNote).ptName}.`);
        return;
      }
      setFeedback('correct');
      setFeedbackText('Certo. +5 XP. Veja a próxima posição.');
      if (guidedIndex + 1 >= (stage.notes?.length ?? 0)) {
        markStageMastered();
      } else {
        window.setTimeout(() => {
          setGuidedIndex((value) => value + 1);
          setFeedback(null);
          setFeedbackText('');
        }, 420);
      }
      return;
    }

    if (stage.kind !== 'drill' && stage.kind !== 'speed') return;
    if (!target || feedback) return;

    const correct = midi === getNote(target).midi;
    const result = registerDrillResult(correct);
    setFeedback(correct ? 'correct' : 'wrong');

    if (result.shouldRollback) {
      setFeedbackText(
        `Seu domínio caiu para ${result.nextMastery}%. Vamos revisar uma etapa anterior antes de continuar.`,
      );
      window.setTimeout(rollbackOneStage, 900);
      return;
    }

    setFeedbackText(
      correct
        ? result.masteredNow
          ? 'Domínio confirmado! +5 XP e esta etapa está concluída.'
          : `Certo. +5 XP · seu domínio subiu para ${result.nextMastery}%.`
        : `Não. Era ${getNote(target).ptName}. Seu domínio caiu para ${result.nextMastery}%.`,
    );

    window.setTimeout(() => {
      if (!result.masteredNow) newTarget();
    }, correct ? 360 : 900);
  };

  useEffect(() => {
    if (stage.kind !== 'speed' || !target || feedback || effectiveMastered) return undefined;
    const limit = stage.timeLimit ?? 0;
    const started = performance.now();
    setTimeLeft(limit);
    timerRef.current = window.setInterval(() => {
      const remaining = Math.max(0, limit - (performance.now() - started) / 1000);
      setTimeLeft(remaining);
      if (remaining <= 0) {
        if (timerRef.current !== null) window.clearInterval(timerRef.current);
        timerRef.current = null;
        const result = registerDrillResult(false);
        setFeedback('wrong');
        if (result.shouldRollback) {
          setFeedbackText(
            `Tempo esgotado. Seu domínio caiu para ${result.nextMastery}%. Vamos revisar uma etapa anterior.`,
          );
          window.setTimeout(rollbackOneStage, 900);
        } else {
          setFeedbackText(
            `Tempo esgotado. Era ${getNote(target).ptName}. Domínio atual: ${result.nextMastery}%.`,
          );
          window.setTimeout(newTarget, 900);
        }
      }
    }, 80);
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [
    effectiveMastered,
    feedback,
    newTarget,
    registerDrillResult,
    rollbackOneStage,
    stage.kind,
    stage.timeLimit,
    target,
  ]);

  const quizOptions = [3, 2, 4, 5, 7];
  const canAdvance =
    reviewAccess ||
    stage.kind === 'intro' ||
    stage.kind === 'done' ||
    effectiveMastered ||
    (stage.kind === 'quiz' && stageScore.mastered);

  const progressPercent = Math.round((stageIndex / (stages.length - 1)) * 100);
  const stageStats = useMemo(
    () => ({
      mastery: masteryScore(stageScore),
      historical: historicalAccuracy(stageScore),
      remaining: policy ? Math.max(0, policy.minAttempts - stageScore.attempts) : 0,
      target: policy?.target ?? 0,
      minAttempts: policy?.minAttempts ?? 0,
    }),
    [policy, stageScore],
  );

  const nextLesson = Math.min(lessonId + 1, 5);

  return (
    <div className="mx-auto w-full max-w-5xl px-3 py-4 sm:px-4 md:py-5">
      <div className="overflow-hidden rounded-[24px] border border-orange-100 bg-white shadow-xl shadow-orange-950/10">
        <div className="border-b border-orange-100 bg-gradient-to-r from-[#fffaf4] via-white to-[#fff3e5] px-4 py-3 md:px-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-orange-600">
                Aula {lessonId} · {config.label}
              </div>
              <div className="mt-0.5 text-sm font-bold text-slate-700">
                Etapa {stageIndex + 1} de {stages.length}
              </div>
              {reviewAccess && (
                <label className="mt-2 block text-xs font-semibold text-orange-800">
                  Acesso completo: escolher etapa
                  <select
                    aria-label="Selecionar etapa para revisão"
                    value={stageIndex}
                    onChange={(event) => { setRollbackNotice(''); setReviewStageIndex(Number(event.target.value)); }}
                    className="mt-1 block max-w-full rounded-lg border border-orange-200 bg-white px-2 py-2 text-sm font-bold text-slate-800"
                  >
                    {stages.map((item, index) => (
                      <option key={item.key} value={index}>{index + 1}. {item.title}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs font-semibold text-slate-600">
              {(stage.kind === 'drill' || stage.kind === 'speed') && policy ? (
                <>
                  <span>{stageScore.attempts} tentativas</span>
                  <span
                    className={
                      stageStats.mastery >= stageStats.target
                        ? 'font-extrabold text-emerald-700'
                        : 'font-extrabold text-orange-700'
                    }
                  >
                    {stageStats.mastery}% domínio
                  </span>
                  <span className="text-slate-400">meta {stageStats.target}%</span>
                </>
              ) : null}
              {stage.kind === 'speed' ? (
                <span className="rounded-full bg-slate-900 px-2.5 py-1 font-mono text-white">
                  {timeLeft.toFixed(1)}s
                </span>
              ) : null}
            </div>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-orange-100">
            <div
              className="h-full rounded-full bg-gradient-to-r from-orange-400 to-amber-500 transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="px-4 py-4 md:px-6 md:py-5">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-orange-600">
            {stage.eyebrow}
          </div>
          <h1 className="mt-1 font-heading text-xl font-extrabold text-slate-950 md:text-2xl">
            {stage.title}
          </h1>
          <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-slate-600">{stage.text}</p>

          {rollbackNotice ? (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
              <i className="ri-arrow-go-back-line mt-0.5 text-lg" />
              <span>{rollbackNotice}</span>
            </div>
          ) : null}

          <div className="mt-4 flex min-h-[220px] items-center justify-center rounded-2xl border border-slate-100 bg-[#fffdf9] p-3 md:min-h-[250px] md:p-4">
            {stage.kind === 'intro' ? (
              <div className="w-full max-w-2xl text-center">
                {stage.introMode === 'clef' ? (
                  <>
                    <div className="relative mx-auto max-w-xl">
                      <StaffNotation notes={['G4']} highlightIndex={0} space={25} />
                      <div className="absolute right-[12%] top-[30%] animate-pulse rounded-full bg-orange-500 px-3 py-1.5 text-xs font-extrabold text-white shadow-lg">
                        2ª linha = Sol
                      </div>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-slate-700">
                      A clave de sol “abraça” a segunda linha. Essa é a nossa primeira referência.
                    </p>
                  </>
                ) : (
                  <>
                    <StaffNotation
                      notes={[config.notes[0], config.notes[Math.floor(config.notes.length / 2)], config.notes[config.notes.length - 1]]}
                      space={lessonId === 4 ? 18 : 23}
                    />
                    <p className="mt-3 text-sm font-semibold text-slate-700">
                      Primeiro observe os extremos e o centro. Depois vamos percorrer cada nota.
                    </p>
                  </>
                )}
              </div>
            ) : null}

            {stage.kind === 'quiz' ? (
              <div className="w-full max-w-xl">
                <div className="mb-5">
                  <StaffNotation notes={[]} space={23} />
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {quizOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        if (option === 5) {
                          setQuizMessage('Isso. O pentagrama tem cinco linhas e quatro espaços.');
                          markStageMastered();
                        } else {
                          setQuizMessage('Ainda não. Conte as linhas de baixo para cima e tente novamente.');
                        }
                      }}
                      className="rounded-xl border border-slate-200 bg-white px-2 py-3 text-xs font-extrabold text-slate-800 transition hover:border-orange-300 hover:bg-orange-50 sm:text-sm"
                    >
                      {option} linhas
                    </button>
                  ))}
                </div>
                {quizMessage ? (
                  <div
                    className={`mt-4 rounded-xl px-4 py-3 text-sm font-semibold ${
                      stageScore.mastered
                        ? 'bg-emerald-50 text-emerald-800'
                        : 'bg-orange-50 text-orange-800'
                    }`}
                  >
                    {quizMessage}
                  </div>
                ) : null}
              </div>
            ) : null}

            {stage.kind === 'demo' ? (
              <div className="w-full max-w-2xl text-center">
                <StaffNotation notes={[currentDemoNote]} highlightIndex={0} space={27} />
                <div className="mt-2 min-h-10">
                  {stage.showNames ? (
                    <span className="inline-flex rounded-full bg-orange-100 px-4 py-2 font-heading text-xl font-extrabold text-orange-800">
                      {getNote(currentDemoNote).ptName}
                    </span>
                  ) : (
                    <span className="text-sm font-semibold text-slate-500">
                      Diga o nome mentalmente antes da próxima nota.
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setDemoIndex(0);
                    setDemoRunning(true);
                  }}
                  disabled={demoRunning}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-extrabold text-white disabled:opacity-50"
                >
                  <i className={demoRunning ? 'ri-loader-4-line animate-spin' : 'ri-play-fill'} />
                  {demoRunning ? 'Acompanhando...' : stageScore.mastered ? 'Repetir sequência' : 'Reproduzir sequência'}
                </button>
              </div>
            ) : null}

            {(stage.kind === 'guided' || stage.kind === 'drill' || stage.kind === 'speed') ? (
              <div className="w-full max-w-2xl text-center">
                <StaffNotation
                  notes={[currentNote]}
                  highlightIndex={0}
                  feedbackIndex={feedback ? 0 : undefined}
                  feedbackType={feedback}
                  space={28}
                />
                <div className="mt-2 flex min-h-9 items-center justify-center">
                  {stage.kind === 'guided' ? (
                    <span className="text-sm font-bold text-slate-600">
                      Nota {guidedIndex + 1} de {stage.notes?.length ?? 0}
                    </span>
                  ) : (
                    <span className="text-sm font-bold text-slate-600">
                      Observe a posição e toque a tecla correspondente.
                    </span>
                  )}
                </div>
                {feedbackText ? (
                  <div
                    className={`mt-2 rounded-xl px-4 py-2.5 text-sm font-semibold ${
                      feedback === 'correct'
                        ? 'bg-emerald-50 text-emerald-800'
                        : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {feedbackText}
                  </div>
                ) : null}
              </div>
            ) : null}

            {stage.kind === 'done' ? (
              <div className="max-w-xl text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                  <i className="ri-trophy-fill text-3xl" />
                </span>
                <h2 className="mt-4 font-heading text-2xl font-extrabold text-slate-950">
                  {config.doneTitle}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{config.doneText}</p>
              </div>
            ) : null}
          </div>

          {(stage.kind === 'guided' || stage.kind === 'drill' || stage.kind === 'speed') ? (
            <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-2.5">
              <PianoKeyboard
                startMidi={config.keyboardStart}
                endMidi={config.keyboardEnd}
                onKeyDown={handleKeyboard}
                showNoteNames={false}
                feedback={
                  feedback && currentNote
                    ? { midi: getNote(currentNote).midi, type: feedback }
                    : null
                }
                className="max-h-[175px]"
              />
              {lessonId === 4 ? (
                <p className="mt-1 text-center text-[11px] font-semibold text-slate-500">
                  No celular, deslize o teclado para os lados para alcançar toda a região.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={stageIndex === 0}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-100 disabled:opacity-30"
            >
              <i className="ri-arrow-left-line" />
              Voltar
            </button>

            <div className="max-w-[260px] text-center text-[11px] font-semibold leading-snug text-slate-500">
              {stage.kind === 'drill' || stage.kind === 'speed'
                ? effectiveMastered
                  ? `Meta atingida · ${stageStats.mastery}% de domínio`
                  : stageStats.remaining > 0
                    ? `Mais ${stageStats.remaining} exercício${stageStats.remaining === 1 ? '' : 's'} mínimo${stageStats.remaining === 1 ? '' : 's'} · domínio ${stageStats.mastery}% · meta ${stageStats.target}%`
                    : `Quantidade mínima concluída · domínio ${stageStats.mastery}% · meta ${stageStats.target}%`
                : stageScore.mastered
                  ? 'Etapa concluída'
                  : ''}
            </div>

            {stage.kind !== 'done' ? (
              <button
                type="button"
                onClick={() => {
                  if (stage.kind === 'intro' && !reviewAccess) markStageMastered();
                  goNext();
                }}
                disabled={!canAdvance}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-extrabold text-white shadow-md shadow-orange-500/20 transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
              >
                Próxima
                <i className="ri-arrow-right-line" />
              </button>
            ) : (
              <a
                href={`/aulas/${nextLesson}`}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white"
              >
                {lessonId === 4 ? 'Ir para simbologia' : 'Próxima região'}
                <i className="ri-arrow-right-line" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
